// components/profile/ProfileForm.jsx
import React, { useState, useEffect } from 'react';
import { useDarkMode } from '../darkmode/DarkModeContext';

const ProfileForm = ({ user, isEditing, onSave, onCancel }) => {
  const { isDarkMode } = useDarkMode();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    dateOfBirth: '',
    gender: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Initialize form data when user data is available
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        dateOfBirth: user.dateOfBirth ? user.dateOfBirth.split('T')[0] : '',
        gender: user.gender || ''
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nama wajib diisi';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Nama minimal 2 karakter';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email wajib diisi';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Format email tidak valid';
    }

    if (formData.phone && !/^[0-9+\-\s()]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Format nomor telepon tidak valid';
    }

    if (formData.dateOfBirth) {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      
      if (age < 13) {
        newErrors.dateOfBirth = 'Usia minimal 13 tahun';
      } else if (age > 120) {
        newErrors.dateOfBirth = 'Tanggal lahir tidak valid';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      // Call the onSave function passed from parent
      await onSave(formData);
    } catch (error) {
      console.error('Error saving profile:', error);
      setErrors({ submit: 'Terjadi kesalahan saat menyimpan profile' });
    } finally {
      setIsLoading(false);
    }
  };

  const inputFieldClass = `w-full px-4 py-3 rounded-lg border transition-all duration-200 ${
    isDarkMode 
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-green-700 focus:bg-gray-600' 
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-green-700 focus:bg-gray-50'
  } focus:outline-none focus:ring-2 focus:ring-green-700/20`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDarkMode ? 'text-gray-200' : 'text-gray-700'
  }`;

  if (!isEditing) {
    // Display mode
    return (
      <div className={`${
        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      } rounded-xl p-6 border shadow-sm transition-colors`}>
        <h2 className={`text-lg font-semibold mb-6 ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>
          Informasi Personal
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className={labelClass}>Nama Lengkap</label>
            <div className={`px-4 py-3 rounded-lg ${
              isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-600'
            }`}>
              {formData.name || 'Belum diisi'}
            </div>
          </div>

          <div>
            <label className={labelClass}>Email</label>
            <div className={`px-4 py-3 rounded-lg ${
              isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-600'
            }`}>
              {formData.email || 'Belum diisi'}
            </div>
          </div>

          <div>
            <label className={labelClass}>Nomor Telepon</label>
            <div className={`px-4 py-3 rounded-lg ${
              isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-600'
            }`}>
              {formData.phone || 'Belum diisi'}
            </div>
          </div>

          <div>
            <label className={labelClass}>Jenis Kelamin</label>
            <div className={`px-4 py-3 rounded-lg ${
              isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-600'
            }`}>
              {formData.gender === 'male' ? 'Laki-laki' : 
               formData.gender === 'female' ? 'Perempuan' : 'Belum diisi'}
            </div>
          </div>

          <div>
            <label className={labelClass}>Tanggal Lahir</label>
            <div className={`px-4 py-3 rounded-lg ${
              isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-600'
            }`}>
              {formData.dateOfBirth 
                ? new Date(formData.dateOfBirth).toLocaleDateString('id-ID')
                : 'Belum diisi'}
            </div>
          </div>

          <div className="md:col-span-2">
            <label className={labelClass}>Alamat</label>
            <div className={`px-4 py-3 rounded-lg ${
              isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-600'
            }`}>
              {formData.address || 'Belum diisi'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Edit mode
  return (
    <div className={`${
      isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    } rounded-xl p-6 border shadow-sm transition-colors`}>
      <h2 className={`text-lg font-semibold mb-6 ${
        isDarkMode ? 'text-white' : 'text-gray-900'
      }`}>
        Edit Informasi Personal
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Name Field */}
          <div>
            <label htmlFor="name" className={labelClass}>
              Nama Lengkap *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={inputFieldClass}
              placeholder="Masukkan nama lengkap"
              disabled={isLoading}
            />
            {errors.name && (
              <p className="text-red-400 text-sm mt-2 flex items-center space-x-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>{errors.name}</span>
              </p>
            )}
          </div>

          {/* Email Field */}
          <div>
            <label htmlFor="email" className={labelClass}>
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={inputFieldClass}
              placeholder="nama@email.com"
              disabled={isLoading}
            />
            {errors.email && (
              <p className="text-red-400 text-sm mt-2 flex items-center space-x-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>{errors.email}</span>
              </p>
            )}
          </div>

          {/* Phone Field */}
          <div>
            <label htmlFor="phone" className={labelClass}>
              Nomor Telepon
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className={inputFieldClass}
              placeholder="+62 812-3456-7890"
              disabled={isLoading}
            />
            {errors.phone && (
              <p className="text-red-400 text-sm mt-2 flex items-center space-x-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>{errors.phone}</span>
              </p>
            )}
          </div>

          {/* Gender Field */}
          <div>
            <label htmlFor="gender" className={labelClass}>
              Jenis Kelamin
            </label>
            <select
              id="gender"
              name="gender"
              value={formData.gender}
              onChange={handleInputChange}
              className={inputFieldClass}
              disabled={isLoading}
            >
              <option value="">Pilih jenis kelamin</option>
              <option value="male">Laki-laki</option>
              <option value="female">Perempuan</option>
            </select>
          </div>

          {/* Date of Birth Field */}
          <div className="md:col-span-2">
            <label htmlFor="dateOfBirth" className={labelClass}>
              Tanggal Lahir
            </label>
            <input
              type="date"
              id="dateOfBirth"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleInputChange}
              className={inputFieldClass}
              disabled={isLoading}
            />
            {errors.dateOfBirth && (
              <p className="text-red-400 text-sm mt-2 flex items-center space-x-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>{errors.dateOfBirth}</span>
              </p>
            )}
          </div>

          {/* Address Field */}
          <div className="md:col-span-2">
            <label htmlFor="address" className={labelClass}>
              Alamat
            </label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              rows="3"
              className={inputFieldClass}
              placeholder="Masukkan alamat lengkap"
              disabled={isLoading}
            />
          </div>
        </div>

        {/* Submit Error */}
        {errors.submit && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3">
            <p className="text-red-400 text-sm flex items-center space-x-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>{errors.submit}</span>
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 px-6 py-3 bg-green-800 hover:bg-green-900 disabled:bg-green-600 text-white font-semibold rounded-lg transition-colors duration-200 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                <span>Menyimpan...</span>
              </div>
            ) : (
              'Simpan Perubahan'
            )}
          </button>

          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className={`px-6 py-3 font-semibold rounded-lg transition-colors duration-200 disabled:cursor-not-allowed ${
              isDarkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 disabled:bg-gray-800 disabled:text-gray-500'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700 disabled:bg-gray-100 disabled:text-gray-400'
            }`}
          >
            Batal
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileForm;